import { IonContent } from "@ionic/react";
import { forwardRef, useEffect, useRef } from "react";
import { cx } from "@/lib/style/cva.config";

export const Content = forwardRef<
  HTMLIonContentElement,
  /**
   * `fullscreen` is er met opzet uit gehaald.
   *
   * Het stond hier wél, maar betekende iets anders dan op `IonContent`: het werd opgevangen en
   * gebruikt om safe-area-marges toe te voegen, en nooit doorgegeven. Wie `<Content fullscreen>`
   * las verwachtte dus dat de inhoud onder de balken door liep, en dat gebeurde niet -- een naam
   * die het tegenovergestelde belooft van wat hij doet.
   *
   * Nu heten de twee dingen naar wat ze zijn: `withSafeAreaPadding` maakt ruimte, en
   * `scrollUnderChrome` laat de inhoud eronderdoor lopen (en zet `fullscreen` op `IonContent`).
   * Weggelaten uit het type, want anders zou hij er stilzwijgend langs komen en twee mensen twee
   * dingen laten betekenen.
   */
  Omit<React.ComponentProps<typeof IonContent>, "fullscreen"> & {
    withTopPadding?: boolean;
    withBottomPadding?: boolean;
    /** Ruimte voor de statusbalk én de home-indicator in één keer. */
    withSafeAreaPadding?: boolean;
    inModal?: boolean;
    /**
     * Laat de inhoud ónder de kop van het scherm door lopen.
     *
     * Dit is wat het glas in `styles/ionic/overrides/header.css` iets te vervagen geeft: zonder dit
     * stopt de inhoud netjes onder de balk en is er niets om doorheen te kijken. Het zet ook de
     * schildwacht aan die `data-scrolled` op de pagina bijhoudt, dus dit is de enige schakelaar —
     * een scherm doet mee of niet, en er is geen half.
     *
     * Ionic vult de ruimte die de kop inneemt zelf aan als `--offset-top` op de scroller, dus de
     * eerste kaart begint nog steeds onder de balk en niet erachter.
     */
    scrollUnderChrome?: boolean;
  }
>(function Content(
  {
    withTopPadding,
    withBottomPadding,
    withSafeAreaPadding,
    inModal,
    scrollUnderChrome,
    children,
    ...props
  },
  ref,
) {
  return (
    <IonContent
      ref={ref}
      fullscreen={scrollUnderChrome}
      {...props}
      className={cx(
        "ion-padding w-full ion-bg-surface-background part-scroll:flex part-scroll:flex-col part-scroll:gap-4",
        (withBottomPadding || withSafeAreaPadding) &&
          "ion-pb-[max(var(--safe-area-inset-bottom),theme(spacing.4))]",
        (withTopPadding || withSafeAreaPadding) &&
          "ion-pt-[max(var(--safe-area-inset-top),theme(spacing.4))]",
        inModal
          ? "plt-desktop:sm:part-scroll:px-4"
          : "plt-desktop:sm:part-scroll:px-[max(calc(calc(100vw-640px)/2),theme(spacing.4))]",
        props.className,
      )}
    >
      {scrollUnderChrome && <ScrollEdgeSentinel />}
      {children}
      {scrollUnderChrome && <ScrollBottomGutter />}
    </IonContent>
  );
});

/**
 * De onderrand van een scherm, als element in de rij en niet als padding.
 *
 * `ion-padding` zet er onderaan zestien pixels bij, maar de scroller is hier een flex-kolom, en in
 * WebKit telt de `padding-bottom` van zo'n scroller niet mee in wat er te scrollen valt. Op een
 * toestel eindigt de lijst daarmee vlak tegen de onderrand: het laatste element staat er half
 * tegenaan, en het stukje dat je mist is er alleen met een rubberbandje bij te trekken -- dat
 * springt terug zodra je loslaat.
 *
 * Meestal is dat laatste element een lijst en valt het niemand op. Op Vandaag is het een knop
 * ("Bekijk alle open diensten"), en dan is het geen schoonheidsfoutje meer maar een knop die je
 * niet kunt indrukken.
 *
 * Een echt element kan geen enkele engine laten vallen. Hij staat hier en niet op de schermen zelf,
 * want dit is de vraag "hoe eindigt een scroller" en die hoort één antwoord te hebben -- en het
 * schermpje dat hem vergeet is precies het schermpje dat als laatste een knop onderaan krijgt.
 *
 * Alleen voor schermen met `scrollUnderChrome`: dat zijn de schermen die echt scrollen. Vensters en
 * de inlogschermen centreren hun inhoud, en daar zou een extra vak die centrering scheeftrekken.
 */
function ScrollBottomGutter() {
  return <div aria-hidden="true" className="h-4 shrink-0" />;
}

/**
 * Houdt bij of er al iets onder de kop door is geschoven.
 *
 * Eén bit — staat de lijst bovenaan, ja of nee — en het antwoord gaat naar het DOM en niet naar
 * React-state: er hangt geen enkele render aan vast, alleen CSS die op `[data-scrolled]` staat te
 * wachten. Een scherm dat scrollt hoort niet te hertekenen omdat het scrollt.
 *
 * Dit was eerst een `IntersectionObserver` op een strookje bovenin de scroller, wat eleganter oogt
 * maar hier niet werkt: `fullscreen` zet de hoogte van de kop als `padding-top` op de scroller, een
 * absoluut geplaatst strookje hangt aan de _padding_-box, en de waarnemer meet tegen de
 * _content_-box. Het strookje zat dus altijd nét buiten beeld, het glas stond altijd aan, en niets
 * viel om — precies het soort fout dat je pas ziet als je het écht tekent.
 *
 * Een passieve `scroll` is hier eerlijker. Hij blokkeert het scrollen niet, leest één getal dat de
 * engine tijdens een scroll toch al bijhoudt, en schrijft alleen iets weg op het moment dat het
 * antwoord omslaat — niet elke frame. Wat we níét doen is Ionic's `ionScroll`: die gaat door React
 * heen en dat is wél een hertekening per frame.
 */
function ScrollEdgeSentinel() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const anchor = ref.current;
    if (!anchor) {
      return;
    }

    const page = anchor.closest(".ion-page");
    const content = anchor.closest("ion-content");
    // `getScrollElement` bestaat pas zodra het custom element is opgewaardeerd. In jsdom en in het
    // ene frame vóór Ionic's hydratie is het er nog niet, en dan is er ook nog niets te meten.
    if (!page || !content || typeof content.getScrollElement !== "function") {
      return;
    }

    let scroller: HTMLElement | undefined;
    let cancelled = false;
    let scrolled: boolean | undefined;

    function onScroll() {
      const next = (scroller?.scrollTop ?? 0) > 0;
      if (next === scrolled) {
        return;
      }

      scrolled = next;
      page?.toggleAttribute("data-scrolled", next);
    }

    // Async omdat Ionic op zijn eigen hydratie wacht. Tussen hier en daar kan het scherm alweer weg
    // zijn — vandaar de vlag, anders luisteren we naar een pagina die niet meer bestaat.
    void content
      .getScrollElement()
      .then((element) => {
        if (cancelled) {
          return;
        }

        scroller = element;
        element.addEventListener("scroll", onScroll, { passive: true });
        // Terugkomen op een scherm dat je halverwege verliet begint niet bovenaan.
        onScroll();
      })
      // Een kop die zijn glas niet krijgt is een schoonheidsfoutje; het scherm eronder werkt gewoon.
      // Dat is geen reden om een afgewezen belofte de logs in te sturen.
      .catch(() => undefined);

    return () => {
      cancelled = true;
      scroller?.removeEventListener("scroll", onScroll);
      // Ionic houdt een verlaten pagina in het DOM voor de terugweg. Zonder dit komt hij terug met
      // het glas nog aan, boven een lijst die bovenaan staat.
      page.removeAttribute("data-scrolled");
    };
  }, []);

  /**
   * Laat de inhoud meebewegen als de kop van hoogte verandert.
   *
   * `fullscreen` compenseert de kop met `--offset-top`, en Ionic meet die hoogte precies twee keer:
   * bij het laden, en bij een `resize` van het venster. Een kop die daarna groeit -- de kalender op
   * Ontdek en Diensten klapt van een week naar een maand -- verstopt dus de bovenkant van de lijst
   * eronder, en er is geen publieke manier om `ion-content` opnieuw te laten meten.
   *
   * Wat er wél is, is die tweede aanleiding. Een `resize` op het venster laat élke `ion-content` op
   * de pagina opnieuw meten; dat is precies wat er moet gebeuren en het is er maar één per keer dat
   * de kalender open- of dichtklapt. Ionic wacht er zelf 100ms mee, en dat valt weg tegen de
   * animatie van het openklappen.
   */
  useEffect(() => {
    const header = ref.current?.closest(".ion-page")?.querySelector(":scope > ion-header");
    if (!header || typeof ResizeObserver === "undefined") {
      return;
    }

    let previous = header.getBoundingClientRect().height;

    const observer = new ResizeObserver(() => {
      const next = header.getBoundingClientRect().height;
      // Een halve pixel is afronding, geen verandering -- en elke dispatch kost een meting op elke
      // gemonteerde pagina.
      if (Math.abs(next - previous) < 0.5) {
        return;
      }

      previous = next;
      window.dispatchEvent(new Event("resize"));
    });

    observer.observe(header);

    return () => observer.disconnect();
  }, []);

  // Het ankertje bestaat alleen om vanuit React bij de juiste `ion-content` en `.ion-page` te komen
  // zonder een ref door `Content` heen te vlechten die een scherm zelf misschien al gebruikt.
  return <div ref={ref} aria-hidden hidden data-testid="scroll-edge-anchor" />;
}
